// src/ utils/geocoding.js
import { devWarn, devLog, logError } from "./logger";

const MAPBOX_API_URL = "https://api.mapbox.com/search/searchbox/v1/reverse";
// Request up to 10 mixed candidates — POIs (cafes, gyms, venues) ranked ahead
// of bare street addresses. Search Box v1 (unlike Geocoding v6) allows
// `limit > 1` with multiple types in one call. Each feature's nested
// `properties.context` carries the same admin levels Geocoding v6 returned,
// so the approximate-precision toggle keeps working without a second request.
const REVERSE_GEOCODE_TYPES = "poi,address";
const REVERSE_GEOCODE_LIMIT = 10;
const TIMEOUT_MS = 5000;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 mins

// In-memory cache for geocoding results
const geocodeCache = new Map();

/**
 * Rounds coordinates to 4 decimal places (~10m precision) for cache key.
 * Higher precision than the previous 3 decimals (~100m) because Search Box can
 * return different POIs at adjacent points within the same 100m grid square —
 * a coffee shop and a gym next door must not share a cache entry.
 */

function getCacheKey(latitude, longitude) {
  const lat = Number(latitude).toFixed(4);
  const lng = Number(longitude).toFixed(4);
  return `${lat},${lng}`;
}

/**
 * Checks if cached entry is still valid
 */
function isCacheValid(entry) {
  if (!entry) return false;
  const age = Date.now() - entry.timestamp;
  return age < CACHE_TTL_MS;
}

/**
 * Formats address for display in modal: "City, State" or "City, Country"
 */
export function formatAddressForDisplay(properties) {
  if (!properties) return "Unknown Location";

  const {
    houseNumber,
    street,
    addressLine1,
    city,
    neighborhood,
    state,
    stateCode,
    country,
    name,
    featureType,
  } = properties;

  // Extract street address
  const streetAddress =
    addressLine1 ||
    (houseNumber && street ? `${houseNumber} ${street}` : null);

  // Choose state format (prefer code)
  const stateDisplay = stateCode || state;

  // Build the address portion with fallbacks, from most-specific to
  // least-specific. Neighborhood is surfaced as its own branch so the
  // neighborhood feature doesn't dedupe against the city feature in the
  // candidate picker.
  const addressPart = (() => {
    if (streetAddress && city && stateDisplay) {
      return `${streetAddress}, ${city}, ${stateDisplay}`;
    } else if (streetAddress && city && country) {
      return `${streetAddress}, ${city}, ${country}`;
    } else if (neighborhood && city && stateDisplay) {
      return `${neighborhood}, ${city}, ${stateDisplay}`;
    } else if (neighborhood && city && country) {
      return `${neighborhood}, ${city}, ${country}`;
    } else if (city && stateDisplay) {
      return `${city}, ${stateDisplay}`;
    } else if (city && country) {
      return `${city}, ${country}`;
    } else if (state && country) {
      return `${state}, ${country}`;
    } else if (state) {
      return state;
    } else if (country) {
      return country;
    }
    return "Unknown Location";
  })();

  // For POI features, prepend the business/place name so the picker shows
  // e.g. "Chef Chengdu Restaurant, 177 West Calaveras Boulevard, Milpitas, CA".
  if (featureType === "poi" && name) {
    return addressPart !== "Unknown Location"
      ? `${name}, ${addressPart}`
      : name;
  }

  return addressPart;
}

/**
 * Formats address for approximate display: "City, State" only (no street).
 * Used during note creation when "Approximate" precision is selected.
 *
 * @param {Object} properties - Normalized geocoding properties
 * @returns {string} City/state level address
 */
export function formatAddressForApproximateDisplay(properties) {
  if (!properties) return "Approximate location";

  const { city, state, stateCode, country } = properties;
  const stateDisplay = stateCode || state;

  if (city && stateDisplay) return `${city}, ${stateDisplay}`;
  if (city && country) return `${city}, ${country}`;
  if (city) return city;
  if (state && country) return `${state}, ${country}`;
  if (state) return state;
  if (country) return country;

  return "Approximate location";
}

/**
 * Extracts place name for notifications (city name)
 */
export function extractPlaceName(properties) {
  if (!properties) return "Unknown Location";

  return (
    properties.city ||
    properties.state ||
    properties.country ||
    "Unknown Location"
  );
}

/**
 * Splits an address string into parts and strips empty parts, zip codes,
 * and common country names. Shared by the display-formatting helpers below.
 */
function filterAddressParts(addressString) {
  if (!addressString) return [];

  const countries = [
    "United States",
    "United States of America",
    "USA",
    "US",
    "Canada",
    "Mexico",
  ];

  return addressString
    .split(",")
    .map((p) => p.trim())
    .filter((part) => {
      if (!part) return false;
      // Zip codes (5 digits or 5+4 format)
      if (/^\d{5}(-\d{4})?$/.test(part)) return false;
      if (countries.includes(part)) return false;
      return true;
    });
}

/**
 * Formats an address STRING for display (not geocoding properties)
 * Strips zip codes and country names to show only "Street, City, State"
 *
 * Examples:
 *   "45060 Synergy St, Fremont, CA 94538, USA" → "45060 Synergy St, Fremont, CA"
 *   "123 Main St, San Francisco, CA" → "123 Main St, San Francisco, CA"
 *   "Fremont, CA" → "Fremont, CA"
 *
 * @param {string} addressString - Full address string
 * @returns {string} Formatted address with only street, city, state
 */
export function formatAddressString(addressString) {
  if (!addressString) return "Unknown Location";

  // Display every meaningful part. filterAddressParts already strips zip codes
  // and country names, so what's left is the address proper. No fixed cap,
  // because POI names can themselves contain commas (e.g. "Earl, Inc.") and a
  // hard slice would truncate the trailing state or city in those cases.
  const result = filterAddressParts(addressString).join(", ");

  return result || "Unknown Location";
}

/**
 * Extracts just the city from an address STRING.
 *
 * Uses the same zip/country filtering as formatAddressString, then picks the
 * second-to-last remaining part when there are 2+ parts (the typical position
 * of "City" in "Street, City, State" or "City, State"), or the only remaining
 * part when there's just one (e.g. a neighborhood-only label like
 * "Mission District").
 *
 * Examples:
 *   "45060 Synergy St, Fremont, CA 94538, USA" → "Fremont"
 *   "123 Main St, San Francisco, CA"           → "San Francisco"
 *   "Fremont, CA"                              → "Fremont"
 *   "Mission District"                         → "Mission District"
 *
 * @param {string} addressString - Full address string
 * @returns {string} City name, or "Unknown Location" if nothing usable
 */
export function extractCityFromAddressString(addressString) {
  if (!addressString) return "Unknown Location";

  const parts = filterAddressParts(addressString);
  if (parts.length === 0) return "Unknown Location";
  if (parts.length === 1) return parts[0];
  return parts[parts.length - 2];
}

/**
 * Performs reverse geocoding with timeout and error handling
 */
async function fetchWithTimeout(url, timeout = TIMEOUT_MS) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === "AbortError") {
      throw new Error("Request timeout");
    }
    throw error;
  }
}

/**
 * Adapts a single Mapbox Search Box v1 feature into the vendor-neutral
 * properties shape consumed by the formatters above. Swapping vendors later
 * only requires rewriting this function — formatters and callers stay
 * untouched. Handles both POI and address features (Geocoding v6 also returned
 * an identical nested-context shape, so this adapter is forward-compatible if
 * the URL ever flips back).
 */
function normalizeMapboxFeature(feature) {
  const props = feature?.properties || {};
  const ctx = props.context || {};

  const addressCtx = ctx.address || {};
  const streetCtx = ctx.street || {};
  const placeCtx = ctx.place || {};
  const localityCtx = ctx.locality || {};
  const neighborhoodCtx = ctx.neighborhood || {};
  const regionCtx = ctx.region || {};
  const countryCtx = ctx.country || {};
  const postcodeCtx = ctx.postcode || {};

  const houseNumber = addressCtx.address_number || undefined;
  const street = streetCtx.name || addressCtx.street_name || undefined;
  const featureType = props.feature_type;
  const name = props.name || undefined;
  const poiCategory = Array.isArray(props.poi_category)
    ? props.poi_category[0]
    : undefined;

  // For address features, properties.name is already pre-formatted as
  // "{number} {street}" and is the most reliable single source. For POIs
  // we don't want to put the business name in addressLine1 — that's the
  // job of the `name` field; addressLine1 should still be the street.
  let addressLine1;
  if (featureType === "address" && name) {
    addressLine1 = name;
  } else if (houseNumber && street) {
    addressLine1 = `${houseNumber} ${street}`;
  } else if (street) {
    addressLine1 = street;
  }

  // Mapbox always renders the full region name in `full_address` /
  // `place_formatted` (e.g. "California", "British Columbia", "Bayern") but
  // also returns the ISO 3166-2 abbreviation in `region_code` ("CA", "BC",
  // "BY"). Substitute the abbreviation everywhere it appears so the saved
  // address matches the conventional postal short form. Works for any
  // country that has region codes in ISO 3166-2; skipped when either field
  // is missing.
  const substituteRegion = (text) => {
    if (!text || !regionCtx.name || !regionCtx.region_code) return text;
    const escaped = regionCtx.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return text.replace(
      new RegExp(`\\b${escaped}\\b`, "g"),
      regionCtx.region_code,
    );
  };
  const fullAddress = substituteRegion(props.full_address);
  const placeFormatted = substituteRegion(props.place_formatted);

  // For POIs, bake the business name into `formatted` so it persists when
  // CreateNoteModal saves the note (see CreateNoteModal.js where the note's
  // address is set from selectedCandidate?.fullAddress). Without this,
  // the POI name would appear in the picker but vanish from the saved note.
  const baseFormatted =
    fullAddress || placeFormatted || name || "Unknown Location";
  // Use a comma between the POI name and the postal address so the saved
  // string matches the picker display. POI names that contain their own
  // commas (e.g. "Earl, Inc.") stay safe because formatAddressString no
  // longer slices to a fixed part count.
  const formatted =
    featureType === "poi" && name && fullAddress
      ? `${name}, ${fullAddress}`
      : baseFormatted;

  return {
    houseNumber,
    street,
    addressLine1,
    city: placeCtx.name || localityCtx.name || undefined,
    neighborhood: neighborhoodCtx.name || undefined,
    state: regionCtx.name || undefined,
    stateCode: regionCtx.region_code || undefined,
    country: countryCtx.name || undefined,
    countryCode: countryCtx.country_code || undefined,
    postcode: postcodeCtx.name || undefined,
    formatted,
    name,
    poiCategory,
    featureType,
  };
}

/**
 * Main reverse geocoding function
 *
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate
 * @param {string} accessToken - Mapbox public access token
 * @returns {Promise<{fullAddress: string, displayAddress: string, properties: object}>}
 */
export async function reverseGeocode(latitude, longitude, accessToken) {
  // validate inputs
  if (!latitude || !longitude) {
    throw new Error("Invalid coordinates");
  }

  if (!accessToken) {
    devWarn("Mapbox access token not configured");
    throw new Error("API key not configured");
  }

  // Check cache first
  const cacheKey = getCacheKey(latitude, longitude);
  const cachedEntry = geocodeCache.get(cacheKey);

  if (isCacheValid(cachedEntry)) {
    devLog("[Geocoding] Using cached result for ", cacheKey);
    return cachedEntry.data;
  }

  // Mapbox v6 uses longitude,latitude order (not lat,lon like Geoapify).
  const url =
    `${MAPBOX_API_URL}` +
    `?longitude=${longitude}` +
    `&latitude=${latitude}` +
    `&types=${REVERSE_GEOCODE_TYPES}` +
    `&limit=${REVERSE_GEOCODE_LIMIT}` +
    `&access_token=${accessToken}`;

  try {
    devLog("[Geocoding] Fetching address for ", cacheKey);
    const response = await fetchWithTimeout(url);

    if (!response.ok) {
      // Capture Mapbox's response body so 401/422/429 surface the real cause
      // (invalid token, unsupported `types` value, rate-limited) in the
      // [Geocoding] Error log instead of just a status code. Truncated to
      // 200 chars to keep logs sane if Mapbox ever returns HTML.
      const body = await response.text().catch(() => "");
      const detail = body ? ` — ${body.slice(0, 200)}` : "";
      throw new Error(`API error: ${response.status}${detail}`);
    }

    const data = await response.json();

    if (!data.features || data.features.length === 0) {
      throw new Error("No results found");
    }

    // Adapt each Mapbox feature into the neutral shape, then dedupe by the
    // rendered displayAddress so visually identical entries don't crowd the
    // picker (common when address detail is absent and several features
    // collapse to the same "City, State" string).
    const seen = new Set();
    const candidates = [];
    for (const feature of data.features) {
      const properties = normalizeMapboxFeature(feature);
      const displayAddress = formatAddressForDisplay(properties);
      if (seen.has(displayAddress)) continue;
      seen.add(displayAddress);
      candidates.push({
        fullAddress: properties.formatted,
        displayAddress,
        properties,
      });
    }

    if (candidates.length === 0) {
      throw new Error("No results found");
    }

    const primary = candidates[0];
    const result = {
      candidates,
      fullAddress: primary.fullAddress,
      displayAddress: primary.displayAddress,
      properties: primary.properties,
    };

    // Cache the result
    geocodeCache.set(cacheKey, {
      data: result,
      timestamp: Date.now(),
    });

    devLog("[Geocoding] Successfully geocoded:", primary.displayAddress);
    return result;
  } catch (error) {
    logError("[Geocoding] Error:", error.message);

    // Return fallback
    const fallback = {
      candidates: [],
      fullAddress: "Unknown Location",
      displayAddress: "Unknown Location",
      properties: null,
    };

    return fallback;
  }
}

/**
 * Clears the geocoding cache
 */
export function clearGeocodeCache() {
  geocodeCache.clear();
}
